/**
 * LengthSnap.js
 *
 * Provides snapping to match the length of the current edge being drawn
 * to the length of a previously drawn, parallel edge. This helps make
 * rectangles (and parallel segments) with equal opposite sides.
 */

import * as THREE from 'three';
import { DrawingGlobals } from "../../Global/variables";

// Angular tolerance (in degrees) for considering edges as parallel
const ANGLE_TOL_DEG = 2;
const COS_TOL = Math.cos(THREE.MathUtils.degToRad(ANGLE_TOL_DEG));

// Distance threshold (world units) under which the cursor will snap
const SNAP_DIST_THRESHOLD = 0.03;

/**
 * Compute a sticky length-snap candidate. If prevSnapPoint is present and the
 * cursor remains within SNAP_DIST_THRESHOLD, keep using it (sticky behavior).
 *
 * The function:
 * - Uses the current pointer candidate "point" and the last placed vertex B
 * - Computes direction dir = normalize(point - B)
 * - Scans previously drawn edges for those parallel to dir within ANGLE_TOL_DEG
 * - For each parallel edge, computes a snap point S = B + dir * length(edge)
 * - If |S - point| <= SNAP_DIST_THRESHOLD, considers it a candidate
 * - Chooses the closest candidate to the cursor and returns it
 *
 * @param {THREE.Vector3|null} prevSnapPoint - Previously snapped length point (sticky)
 * @param {THREE.Vector3} point - Current cursor candidate world position (or 90° snap point)
 * @returns {THREE.Vector3|null} Length snap point or null if none
 */
export function lengthSnap(prevSnapPoint, point) {
  // Sticky retain behavior
  if (prevSnapPoint && point.distanceTo(prevSnapPoint) <= SNAP_DIST_THRESHOLD) {
    // keep the same matched edges as previous frame
    return prevSnapPoint;
  }

  const pts = DrawingGlobals.intersectionPoints;
  if (!pts || pts.length < 2) {
    DrawingGlobals.lengthSnapMatchedEdges = [];
    return null;
  }

  // Last placed vertex B
  const B = pts[pts.length - 1];

  // Direction from B to current pointer
  const dir = point.clone().sub(B);
  const len = dir.length();
  if (len < 1e-6) {
    DrawingGlobals.lengthSnapMatchedEdges = [];
    return null; // avoid degenerate
  }
  dir.divideScalar(len); // normalize

  // Build list of previously drawn edges from the polyline
  const edges = [];
  for (let i = 0; i < pts.length - 1; i++) {
    const p1 = pts[i];
    const p2 = pts[i + 1];
    const e = p2.clone().sub(p1);
    const elen = e.length();
    if (elen < 1e-6) continue;
    const edir = e.clone().divideScalar(elen);
    edges.push({ i, p1, p2, edir, elen });
  }

  // Find parallel edges to current direction
  const candidates = [];
  for (const { i, p1, p2, edir, elen } of edges) {
    const dot = Math.abs(edir.dot(dir));
    if (dot >= COS_TOL) {
      // Compute snap point at the matched length from B along current direction
      const S = B.clone().add(dir.clone().multiplyScalar(elen));
      const d = S.distanceTo(point);
      if (d <= SNAP_DIST_THRESHOLD) {
        candidates.push({ i, S, elen, d });
      }
    }
  }

  if (candidates.length === 0) {
    DrawingGlobals.lengthSnapMatchedEdges = [];
    return null;
  }

  // Pick closest candidate
  candidates.sort((a, b) => a.d - b.d);
  const chosen = candidates[0];

  // store which edge index matched for highlighting
  DrawingGlobals.lengthSnapMatchedEdges = [chosen.i];

  // Debug logs
  console.log(
    `LengthSnap: candidates=${candidates.length}, chosenEdgeIndex=${chosen.i}, lengthMeters=${chosen.elen.toFixed(3)}, distance=${chosen.d.toFixed(3)}`
  );

  return chosen.S;
}
